import { FormField, FormItem, FormLabel, FormMessage, MultiSelect, RankFunction } from "@/shared/components";
import { FieldPath, FieldValues, UseFormReturn } from "react-hook-form";

type Option = { value: string; label: string };

interface MultiSelectFormFieldProps<T extends FieldValues> {
    form: UseFormReturn<T>;
    label?: string;
    name: FieldPath<T>;
    options: Option[];
    placeholder?: string;
    search?: { placeholder: string; emptyMessage: string };
    className?: string;
    "data-testid"?: string;
    labelClassName?: string;
    optional?: boolean;
    triggerFormValidationOnChange?: boolean;
    helpText?: string;
    rankFn?: RankFunction;
}

export function MultiSelectFormField<T extends FieldValues>({
    form,
    label,
    name,
    search,
    placeholder,
    options,
    className,
    labelClassName,
    optional,
    triggerFormValidationOnChange = false,
    helpText,
    rankFn,
    "data-testid": dataTestid,
}: Readonly<MultiSelectFormFieldProps<T>>) {
    return (
        <FormField
            control={form.control}
            name={name}
            render={({ field }) => (
                <FormItem>
                    {label && (
                        <FormLabel optional={optional} className={labelClassName}>
                            {label}
                        </FormLabel>
                    )}
                    <MultiSelect
                        onValueChange={(value) => {
                            field.onChange(value);
                            if (triggerFormValidationOnChange) void form.trigger(name);
                        }}
                        value={field.value}
                        options={options}
                        search={search}
                        placeholder={placeholder}
                        aria-label={label}
                        data-testid={dataTestid}
                        className={className}
                        rankFn={rankFn}
                    />
                    {helpText && <span className="text-content-subtle text-s leading-l">{helpText}</span>}
                    <FormMessage />
                </FormItem>
            )}
        />
    );
}
